import type React from "react";
import CustomLink from "@components/atoms/Link";
import Text from "@components/atoms/Text";
import styles from "./VariantCard.module.scss";
import { VariantCardProps } from "./VariantCard.types";
import Tag from "@components/atoms/Tag";
import { formatCurrencyINR, maskAmount } from "@utils/numberFormatter";
import ButtonClientWrapper from "./ButtonClientWrapper";
import { calculateEmi } from "@utils/emiCalculator";
import { formatDeliveryDate } from "@utils/formatDate";
import Image from "next/image";
import assetLoader from "@utils/internalImageLoder";
import AppTrackedAnchor from "./AppTrackedAnchor";

const VariantCard: React.FC<VariantCardProps> = ({
  title,
  fuelType,
  engineCapacity,
  transmission,
  mileage,
  price,
  originalPrice,
  savings,
  location,
  badgeType,
  className = "",
  isLoggedIn,
  variantType,
  variantSlug,
  brandName,
  modelName,
  isSellable,
  modelStatus,
  pageType,
  carAvailability,
  isInApp,
  modelSlug,
  expressDelivery,
  expressDeliveryDays,
  standardDeliveryDays,
}) => {
  const numericPrice = Number(price) || 0;
  const numericOriginalPrice = Number(originalPrice) || 0;
  const numericSavings = Number(savings) || 0;
  const hasOriginalPrice = numericOriginalPrice > 0;
  const hasSavings = numericSavings > 0;

  if (isInApp) {
    const emiAmount = calculateEmi(numericPrice);
    const deliveryDays = expressDelivery ? expressDeliveryDays : standardDeliveryDays;
    const variantDetailsHref = modelSlug && variantSlug
      ? `/acko-app/cars/${variantSlug}`
      : `/cars/${variantSlug || ""}`;

    return (
      <div className={`${styles.card} ${className}`}>
        {badgeType && (
          <Tag
            label={badgeType}
            variant="variant-card"
            variantType={variantType}
          />
        )}

        <Text className={styles.title}>{title}</Text>

        <div className={styles.appSpecs}>
          <Text>{fuelType}</Text>
          {engineCapacity && (
            <>
              <span className={styles.dot}>•</span>
              <Text>{engineCapacity}</Text>
            </>
          )}
          {transmission && (
            <>
              <span className={styles.dot}>•</span>
              <Text>{transmission}</Text>
            </>
          )}
          {mileage && (
            <>
              <span className={styles.dot}>•</span>
              <Text>{mileage}</Text>
            </>
          )}
        </div>

        {!isSellable ? (
          <>
            <Text level="span" className={styles.appLocation}>
              {`On-road price in ${location}`}
            </Text>

            <div className={styles.priceContainer}>
              <Text className={styles.ackodrivePrice}>
                {formatCurrencyINR(originalPrice)}
              </Text>
            </div>

            <AppTrackedAnchor
              className={styles.helpMeBuyButton}
              href={variantDetailsHref}
              pageType={pageType}
              eventProperties={{
                action_type: "tap_btn",
                action_name: "tap_btn_variants_see_details",
                section: "Variant",
                variant_name: title,
                car_availability: carAvailability,
                city_name: location,
                vehicle_make: brandName,
                vehicle_model: modelName,
                vehicle_variant: title,
              }}
            >
              View details
            </AppTrackedAnchor>

          </>
        ) : (
          <>
            <Text level="span" className={styles.appLocation}>
              {`ACKO Drive price in ${location}`}
            </Text>

            <div className={styles.priceContainer}>
              <Text className={styles.ackodrivePrice}>
                {formatCurrencyINR(price)}
              </Text>
              {hasOriginalPrice && (
                <Text className={styles.marketPrice}>
                  {formatCurrencyINR(originalPrice)}
                </Text>
              )}
              {hasSavings && (
                <Text className={styles.appSavings}>
                  {`Save ${formatCurrencyINR(savings)}`}
                </Text>
              )}
            </div>

            {emiAmount > 0 && (
              <Text className={styles.appEmiText}>
                {`EMI from ₹${emiAmount.toLocaleString("en-IN")}`}
              </Text>
            )}

            {deliveryDays && (
              <div className={styles.appDeliveryInfo}>
                {expressDelivery ? (
                  <>
                    <Text className={styles.appExpressDelivery}>
                      {`Express delivery by ${formatDeliveryDate(deliveryDays)}`}
                    </Text>
                    <Image
                      src={`${assetLoader()}/icons/thunder.svg`}
                      alt="thunder"
                      width={12}
                      height={12}
                      loading="lazy"
                    />
                  </>
                ) : (
                  <>
                    <Text className={styles.appStandardDelivery}>
                      {`Delivery by ${formatDeliveryDate(deliveryDays)}`}
                    </Text>
                    <Image
                      src={`${assetLoader()}/icons/clockTime.webp`}
                      alt="wait"
                      width={12}
                      height={12}
                    />
                  </>
                )}
              </div>
            )}

            <AppTrackedAnchor
              className={styles.helpMeBuyButton}
              href={variantDetailsHref}
              pageType={pageType}
              eventProperties={{
                action_type: "tap_btn",
                action_name: "tap_btn_variants_see_details",
                section: "Variant",
                variant_name: title,
                car_availability: carAvailability,
                city_name: location,
                vehicle_make: brandName,
                vehicle_model: modelName,
                vehicle_variant: title,
              }}
            >
              View details
            </AppTrackedAnchor>
          </>
        )}
      </div>
    );
  }

  return (
    <div className={`${styles.card} ${className}`}>
      {badgeType && (
        <Tag
          label={badgeType}
          variant="variant-card"
          variantType={variantType}
        />
      )}

      <CustomLink
        className={styles.title}
        href={`/cars/${variantSlug}`}
        segmentEventProps={{
          eventName: "action",
          pageType: pageType,
          eventProperties: {
            action_type: "tap_btn",
            action_name: "tap_btn_variant_title_mm",
            section: "Variant",
            variant_name: title,
            car_availability: carAvailability,
            city_name: location,
            vehicle_make: brandName,
            vehicle_model: modelName,
            vehicle_variant: title,
          },
        }}
      >
        {title}
      </CustomLink>

      <div className={styles.specs}>
        <Text>{fuelType}</Text>

        {engineCapacity && (
          <>
            <span className={styles.dot}>•</span>
            <Text>{engineCapacity}</Text>
          </>
        )}
        {transmission && (
          <>
            <span className={styles.dot}>•</span>
            <Text>{transmission}</Text>
          </>
        )}
        {mileage && (
          <>
            <span className={styles.dot}>•</span>
            <Text>{mileage}</Text>
          </>
        )}
      </div>

      {!isSellable && (
        <>
          <Text level="span" className={styles.location}>
            {`On-road price in ${location}`}
          </Text>

          <div className={styles.priceContainer}>
            <Text className={styles.ackodrivePrice}>
              {formatCurrencyINR(originalPrice)}
            </Text>
          </div>
          <CustomLink
            size="ds"
            className={styles.exploreButton}
            href={`/cars/${variantSlug}`}
            segmentEventProps={{
              eventName: "action",
              pageType: pageType,
              eventProperties: {
                action_type: "tap_btn",
                action_name: "tap_btn_variants_see_details",
                section: "Variant",
                variant_name: title,
                car_availability: carAvailability,
                city_name: location,
                vehicle_make: brandName,
                vehicle_model: modelName,
                vehicle_variant: title,
              },
            }}
          >
            Explore this variant
          </CustomLink>
        </>
      )}

      {isSellable && (
        <>
          {isLoggedIn ? (
            <Text level="span" className={styles.location}>
              {`ACKO Drive price in ${location}`}
            </Text>
          ) : (
            <Text level="span" className={styles.location}>
              {`On-road price in ${location}`}
            </Text>
          )}

          <div className={styles.priceContainer}>
            <Text className={styles.ackodrivePrice}>
              {formatCurrencyINR(price)}
            </Text>
            {isLoggedIn && hasOriginalPrice && (
              <Text className={styles.marketPrice}>
                {formatCurrencyINR(originalPrice)}
              </Text>
            )}
            {isLoggedIn && hasSavings && (
              <Text className={styles.savings}>
                {`Save ${formatCurrencyINR(savings)}`}
              </Text>
            )}
          </div>

          {isLoggedIn ? (
            <CustomLink
              size="ds"
              className={styles.exploreButton}
              href={`/cars/${variantSlug}`}
              segmentEventProps={{
                eventName: "action",
                pageType: pageType,
                eventProperties: {
                  action_type: "tap_btn",
                  action_name: "tap_btn_variants_see_details",
                  section: "Variant",
                  variant_name: title,
                  car_availability: carAvailability,
                  city_name: location,
                  vehicle_make: brandName,
                  vehicle_model: modelName,
                },
              }}
            >
              Explore this variant
            </CustomLink>
          ) : (
            <ButtonClientWrapper
              brandName={brandName}
              modelName={modelName}
              title={title}
              savings={savings}
              pageType={pageType}
              location={location}
              carAvailability={carAvailability}
              variantSlug={variantSlug}
            />
          )}
        </>
      )}
    </div>
  );
};

export default VariantCard;
